import type { CreateOAuthApp } from '@wf-coe/types';
import {
  Button,
  HelperText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
} from 'flowbite-react';
import { type FormEvent, useEffect, useState } from 'react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import { SecretsRowButton } from '../../components/clients/SecretsRowButton.js';

const OAUTH_APP_BUNDLE_FIELDS = [
  { key: 'privateKey', label: 'Private key (PEM)', kind: 'string' as const },
  { key: 'clientAppSecret', label: 'Client app secret', kind: 'string' as const },
];
import { useParams } from 'react-router-dom';
import { useCreateOAuthAppMutation } from '../../queries/use-create-oauth-app.js';
import { useDeleteOAuthAppMutation } from '../../queries/use-delete-oauth-app.js';
import { type OAuthAppDto, useOAuthAppsQuery } from '../../queries/use-oauth-apps.js';
import { useUpdateOAuthAppMutation } from '../../queries/use-update-oauth-app.js';

/**
 * `/clients/{id}/oauth-apps` sub-tab — manages Workfront OAuth app
 * credentials per tenant.
 *
 * The public columns (name, customer_id, user_id, client_app_id) live
 * here; secret fields (client_app_secret, private_key) land with the
 * per-tenant Secrets Manager bundle slice.
 */
export function OAuthApps(): JSX.Element {
  const { id: clientId } = useParams<{ id: string }>();
  const { data, error, isLoading, isError } = useOAuthAppsQuery(clientId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<OAuthAppDto | null>(null);
  const [deleting, setDeleting] = useState<OAuthAppDto | null>(null);

  const handleNew = (): void => {
    setEditing(null);
    setEditorOpen(true);
  };
  const handleEdit = (app: OAuthAppDto): void => {
    setEditing(app);
    setEditorOpen(true);
  };

  return (
    <section>
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">OAuth Apps</h2>
        <Button size="sm" onClick={handleNew} disabled={!clientId}>
          <FiPlus className="mr-1" /> New OAuth app
        </Button>
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400" role="status">
          Loading OAuth apps…
        </p>
      ) : isError ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
        >
          {error instanceof Error ? error.message : 'Failed to load OAuth apps'}
        </div>
      ) : data && data.length === 0 ? (
        <EmptyState onCreate={handleNew} />
      ) : data ? (
        <OAuthAppsTable
          apps={data}
          clientId={clientId}
          onEdit={handleEdit}
          onDelete={setDeleting}
        />
      ) : null}

      {clientId ? (
        <OAuthAppEditModal
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          clientId={clientId}
          existing={editing}
        />
      ) : null}
      {clientId && deleting ? (
        <OAuthAppDeleteModal
          open={Boolean(deleting)}
          onClose={() => setDeleting(null)}
          clientId={clientId}
          app={deleting}
        />
      ) : null}
    </section>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center dark:border-gray-600 dark:bg-gray-800">
      <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">No OAuth apps yet</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
        Add a Workfront OAuth app so audit + backup jobs can authenticate as this tenant.
      </p>
      <Button size="sm" className="mx-auto mt-4" onClick={onCreate}>
        <FiPlus className="mr-1" /> New OAuth app
      </Button>
    </div>
  );
}

function OAuthAppsTable({
  apps,
  clientId,
  onEdit,
  onDelete,
}: {
  apps: OAuthAppDto[];
  clientId: string | undefined;
  onEdit: (app: OAuthAppDto) => void;
  onDelete: (app: OAuthAppDto) => void;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeadCell>Name</TableHeadCell>
            <TableHeadCell>Client App ID</TableHeadCell>
            <TableHeadCell>Customer ID</TableHeadCell>
            <TableHeadCell>User ID</TableHeadCell>
            <TableHeadCell>
              <span className="sr-only">Actions</span>
            </TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody className="divide-y divide-gray-200 dark:divide-gray-700">
          {apps.map((app) => (
            <TableRow key={app.id} className="bg-white dark:bg-gray-800">
              <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                {app.name}
              </TableCell>
              <TableCell className="font-mono text-xs">{app.clientAppId}</TableCell>
              <TableCell className="font-mono text-xs">{app.customerId ?? '—'}</TableCell>
              <TableCell className="font-mono text-xs">{app.userId ?? '—'}</TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-2">
                  {clientId ? (
                    <SecretsRowButton
                      bundlePath={`/v1/clients/${clientId}/oauth-apps/${app.id}`}
                      fields={OAUTH_APP_BUNDLE_FIELDS}
                      title={`${app.name} — secrets`}
                      ariaLabel={`Manage secrets for ${app.name}`}
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onEdit(app)}
                    aria-label={`Edit ${app.name}`}
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(app)}
                    aria-label={`Delete ${app.name}`}
                    className="rounded p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950 dark:hover:text-red-300"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function OAuthAppEditModal({
  open,
  onClose,
  clientId,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  existing: OAuthAppDto | null;
}): JSX.Element {
  const create = useCreateOAuthAppMutation();
  const update = useUpdateOAuthAppMutation();
  const [name, setName] = useState('');
  const [clientAppId, setClientAppId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [userId, setUserId] = useState('');

  useEffect(() => {
    if (open) {
      setName(existing?.name ?? '');
      setClientAppId(existing?.clientAppId ?? '');
      setCustomerId(existing?.customerId ?? '');
      setUserId(existing?.userId ?? '');
      create.reset();
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id]);

  const isPending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const body: CreateOAuthApp = {
      name: name.trim(),
      clientAppId: clientAppId.trim(),
      customerId: customerId.trim() === '' ? null : customerId.trim(),
      userId: userId.trim() === '' ? null : userId.trim(),
    };
    if (existing) {
      update.mutate({ clientId, appId: existing.id, patch: body }, { onSuccess: onClose });
    } else {
      create.mutate({ clientId, body }, { onSuccess: onClose });
    }
  };

  const canSubmit = name.trim() !== '' && clientAppId.trim() !== '' && !isPending;

  return (
    <Modal show={open} onClose={onClose} dismissible size="lg">
      <ModalHeader>{existing ? 'Edit OAuth app' : 'New OAuth app'}</ModalHeader>
      <form onSubmit={handleSubmit} aria-label="OAuth app form">
        <ModalBody>
          <div className="space-y-4">
            <div>
              <Label htmlFor="oauth-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="oauth-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isPending}
              />
            </div>
            <div>
              <Label htmlFor="oauth-client-app-id">
                Client App ID <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="oauth-client-app-id"
                type="text"
                required
                value={clientAppId}
                onChange={(e) => setClientAppId(e.target.value)}
                disabled={isPending}
              />
              <HelperText className="mt-1">
                The Adobe IMS / Workfront OAuth client identifier. Public, not a secret.
              </HelperText>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="oauth-customer-id">Customer ID</Label>
                <TextInput
                  id="oauth-customer-id"
                  type="text"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  disabled={isPending}
                />
              </div>
              <div>
                <Label htmlFor="oauth-user-id">User ID</Label>
                <TextInput
                  id="oauth-user-id"
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  disabled={isPending}
                />
              </div>
            </div>
            {error ? (
              <div
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
              >
                {error instanceof Error ? error.message : 'Save failed'}
              </div>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isPending ? 'Saving…' : existing ? 'Save' : 'Create'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function OAuthAppDeleteModal({
  open,
  onClose,
  clientId,
  app,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  app: OAuthAppDto;
}): JSX.Element {
  const del = useDeleteOAuthAppMutation();

  const handleConfirm = (): void => {
    del.mutate({ clientId, appId: app.id }, { onSuccess: onClose });
  };

  return (
    <Modal show={open} onClose={onClose} dismissible size="md">
      <ModalHeader>Delete OAuth app</ModalHeader>
      <ModalBody>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          This will permanently delete <span className="font-semibold">{app.name}</span>. Any
          Workfront / Fusion configs referencing it lose their link (set to null) — the configs
          themselves stay.
        </p>
        {del.isError ? (
          <div
            role="alert"
            className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {del.error instanceof Error ? del.error.message : 'Failed to delete'}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={onClose} disabled={del.isPending}>
          Cancel
        </Button>
        <Button color="failure" onClick={handleConfirm} disabled={del.isPending}>
          {del.isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
